import {KitchenService} from '../KitchenService';
import {Color, DoubleSide, Mesh, MeshBasicMaterial, Object3D, RepeatWrapping, Texture, TextureLoader} from 'three';
import {LoadingManager} from 'three/src/loaders/LoadingManager';
import {ITextureCache} from '../../../interfaces/ITextureCache';
import {IMaterialTextures} from '../../../interfaces/IMaterialTextures';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {
    FACADE_DETAIL_TYPE_BACK, FACADE_DETAIL_TYPE_CUSTOM, FACADE_DETAIL_TYPE_CUSTOM_GLASS, FACADE_DETAIL_TYPE_GENERAL,
    FACADE_DETAIL_TYPE_GLASS, FACADE_DETAIL_TYPE_GRID,
    FACADE_DETAIL_TYPE_MAIN, FACADE_DETAIL_TYPE_PATINA, FACADE_DETAIL_TYPE_SECOND,
    FACADE_MODEL_VERSION_1, FACADE_MODEL_VERSION_2, FACADE_MODEL_VERSION_NONE
} from '../../../../../common-code/constants';
import {ThreeFacade} from '../../../objects/threeD/details/ThreeFacade/ThreeFacade';
import {CommonObject} from '../../../objects/CommonObject/CommonObject';
import {ThreeEquipment} from '../../../objects/threeD/equipments/ThreeEquipment/ThreeEquipment';
import {ThreeHandle} from '../../../objects/threeD/details/ThreeHandle/ThreeHandle';
import {HIDE_TEXTURE_LOADING, LOADED_TEXTURES, SHOW_TEXTURE_LOADING} from '../../../../constants';
import {TCacheTexture} from '../../../types/TCacheTexture';
import {TCacheModel} from '../../../types/TCacheModel';
import {TLoadingModel} from '../../../types/TLoadingModel';
import {ITextureData} from '../../../../../common-code/interfaces/materials/ITextureData';
import {CommonHelper} from 'common-code';
import {
    ThreeConstructiveModel
} from '../../../objects/threeD/constructive/ThreeConstructiveModel/ThreeConstructiveModel';
import {IFacadeModelData} from '../../../../../common-code/interfaces/materials/IFacadeModelData';
import {
    OFFLINE_CONSTRUCTIVE,
    OFFLINE_EQUIPMENT,
    OFFLINE_FACADE,
    OFFLINE_HANDLE,
    OFFLINE_TEXTURE
} from '../../../constants';
import {IEquipmentModelData} from '../../../../../common-code/interfaces/IEquipmentModelData';
import {IConstructiveModelData} from '../../../../../common-code/interfaces/room/IConstructiveModelData';
import {IHandleModelData} from '../../../../../common-code/interfaces/materials/IHandleModelData';
import {ITextureChangeData} from '../../../../../common-code/interfaces/materials/ITextureChangeData';
import {TTextureType} from '../../../../../common-code/types/TTextureType';
import {IHandleData} from '../../../../../common-code/interfaces/materials/IHandleData';
import {TFacadeModelVersion} from '../../../../../common-code/types/facade/TFacadeModelVersion';
import {TPoint3D} from '../../../../../common-code/types/TPoint3D';
import {TFacadeDetailType} from '../../../../../common-code/types/facade/TFacadeDetailType';
import {IModelTransformData} from '../../../../../common-code/interfaces/3d/IModelTransformData';
import {IDetailNameTransformData} from '../../../../../common-code/interfaces/3d/IDetailNameTransformData';
import {ThreeLoadModelCorpus} from '../../../objects/threeD/details/ThreeCorpus/types/ThreeLoadModelCorpus';
import {ICorpusModelData} from '../../../../../../../common/interfaces/3d/ICorpusModelData';

export class CacheManager {
    service: KitchenService;
    textures: ITextureCache;
    textureLoader: TextureLoader;
    gltfLoader: GLTFLoader;
    cacheTextures: { [id: string]: TCacheTexture };
    cacheTextureImages: { [id: string]: Texture };
    cacheModels: { [id: string]: TCacheModel };
    loadingModels: { [id: string]: TLoadingModel };
    loading: boolean;
    sketchMaterials: {
        default: MeshBasicMaterial;
        room: MeshBasicMaterial;
    }

    constructor(service: KitchenService, manager?: LoadingManager) {
        this.service = service;
        this.textures = CacheManager.initTextures();
        this.textureLoader = new TextureLoader(manager);
        this.gltfLoader = new GLTFLoader(manager);
        this.cacheTextures = {};
        this.cacheTextureImages = {};
        this.cacheModels = {};
        this.loadingModels = {};
        this.sketchMaterials = CacheManager.initSketchMaterials();
        this.loading = false;
    }

    public remove() {
        this.cacheTextures = {};
        this.cacheTextureImages = {};
        this.cacheModels = {};
        this.loadingModels = {};
    }

    public getDefaultSketchViewMaterial():MeshBasicMaterial {
        return  this.sketchMaterials.default;
    }

    public getRoomSketchViewMaterial():MeshBasicMaterial {
        return  this.sketchMaterials.room;
    }

    public loadCorpusThreeModel(corpus: ThreeLoadModelCorpus) {
        let object: Object3D;
        let modelPath: string;
        let cacheModelId: string;
        let threeModelData: ICorpusModelData;

        if (!corpus.threeModelData || this.service.isOffline()) {
            return;
        }
        threeModelData = corpus.threeModelData;
        cacheModelId = 'corpus' + threeModelData.id;
        this.setLoadingModels('corpus', threeModelData.id, corpus);
        if (this.cacheModels[cacheModelId] &&
            this.cacheModels[cacheModelId].isLoad) {
            this.clearLoadingModel('corpus', threeModelData.id, corpus);
            return;
        }
        modelPath = '/static-files/corpusModel/' + threeModelData.id.substring(0, 3) + '/' +
            threeModelData.id + '.' + threeModelData.ext;
        this.cacheModels[cacheModelId] = {isLoad: false, details: []};
        this.startTextureLoading();
        this.gltfLoader.load(modelPath, (gltf) => {
                let objects: Object3D[] = [];
                for (object of gltf.scene.children) {
                    objects.push(object);
                }
                this.cacheModels[cacheModelId].isLoad = true;
                this.cacheModels[cacheModelId].details = objects;
                this.clearLoadingModel('corpus', threeModelData.id, corpus);
            }, () => {
            },
            () => {
                this.clearLoadingModel('corpus', threeModelData.id, corpus);
            });
    }

    public loadFacadeThreeModel(facade: ThreeFacade) {
        let object: Object3D;
        let modelPath: string;
        let cacheModelId: string;
        let threeModelData: IFacadeModelData;

        if (!facade.threeModelData) {
            return;
        }
        threeModelData = facade.threeModelData;
        cacheModelId = 'facade' + threeModelData.id;
        this.setLoadingModels('facade', threeModelData.id, facade);
        if (this.cacheModels[cacheModelId] &&
            this.cacheModels[cacheModelId].isLoad) {
            this.clearLoadingModel('facade', threeModelData.id, facade);
            return;
        }
        modelPath = this.service.isOffline() ? OFFLINE_FACADE :
            '/static-files/facade/' + threeModelData.id.substring(0, 3) + '/' +
            threeModelData.id + '.' + threeModelData.ext;
        this.cacheModels[cacheModelId] = {isLoad: false, details: []};
        this.startTextureLoading();
        this.gltfLoader.load(modelPath, (gltf) => {
            let objects: Object3D[] = [];
             for (object of gltf.scene.children) {
                object.receiveShadow = true
                object.castShadow = true
                // Дизайнер назвал все Mesh с большой буквы
                object.name = object.name.toLocaleLowerCase();
                if ('isMesh' in object &&
                    object instanceof Mesh) {
                    object.userData.transformData = this.getFacadeTransformData(object.name);
                }
                object.traverse((child) => {
                    if ('isMesh' in child &&
                        child instanceof Mesh) {
                        child.name = child.name.toLocaleLowerCase();
                        child.userData.transformData = this.getFacadeTransformData(child.name);
                    }
                })
                objects.push(object);
            }
            this.cacheModels[cacheModelId].isLoad = true;
            this.cacheModels[cacheModelId].details = objects;
            this.clearLoadingModel('facade', threeModelData.id, facade);
        }, () => {},
            () => {
                this.clearLoadingModel('facade', threeModelData.id, facade);
            });
    }

    public loadThreeModel(commonObject: CommonObject, modelId: string) {
        let object: Object3D;
        let modelPath: string;
        let cacheModelId: string;

        cacheModelId = 'loadModel' + modelId;
        this.setLoadingModels('loadModel', modelId, commonObject);
        if (this.cacheModels[cacheModelId] &&
            this.cacheModels[cacheModelId].isLoad) {
            this.clearLoadingModel('loadModel', modelId, commonObject);
            return;
        }

        modelPath = '/static-files/threeModel/' + modelId.substring(0, 3) + '/' +
            modelId + '.glb';
        this.cacheModels[cacheModelId] = {isLoad: false, details: []};
        this.startTextureLoading();
        this.gltfLoader.load(modelPath, (gltf) => {
            let objects: Object3D[] = [];
            for (object of gltf.scene.children) {
                object.castShadow = true;
                object.receiveShadow = true;
                objects.push(object);
            }
            this.cacheModels[cacheModelId].isLoad = true;
            this.cacheModels[cacheModelId].details = objects;
            this.clearLoadingModel('loadModel', modelId, commonObject);
        }, () => {},
            () => {
                this.clearLoadingModel('loadModel', modelId, commonObject);
            });
    }

    public loadEquipmentThreeModel(equipment: ThreeEquipment) {
        let object: Object3D;
        let modelPath: string;
        let cacheModelId: string;
        let threeModelData: IEquipmentModelData;

        if (equipment.threeModelData === undefined) {
            return;
        }
        threeModelData = equipment.threeModelData;
        cacheModelId = 'equipment' + threeModelData.id;
        this.setLoadingModels('equipment', threeModelData.id, equipment);
        if (this.cacheModels[cacheModelId] &&
            this.cacheModels[cacheModelId].isLoad) {
            this.clearLoadingModel('equipment', threeModelData.id, equipment);
            return;
        }

        modelPath = this.service.isOffline() ? OFFLINE_EQUIPMENT :
            '/static-files/equipmentModel/' + threeModelData.id.substring(0, 3) + '/' +
            threeModelData.id + '.' + threeModelData.ext;
        this.cacheModels[cacheModelId] = {isLoad: false, details: []};
        this.startTextureLoading();
        this.gltfLoader.load(modelPath, (gltf) => {
            let objects: Object3D[] = [];
            for (object of gltf.scene.children) {
                object.castShadow = true;
                object.receiveShadow = true;
                objects.push(object);
            }
            this.cacheModels[cacheModelId].isLoad = true;
            this.cacheModels[cacheModelId].details = objects;
            this.clearLoadingModel('equipment', threeModelData.id, equipment);
        }, () => {},
            () => {
                this.clearLoadingModel('equipment', threeModelData.id, equipment);
            });
    }

    public loadConstructiveThreeModel(constructive: ThreeConstructiveModel) {
        let object: Object3D;
        let modelPath: string;
        let cacheModelId: string;

        if (!constructive.threeModelData) {
            return;
        }
        const threeModelData: IConstructiveModelData = constructive.threeModelData;
        cacheModelId = 'constructive' + threeModelData.id;
        this.setLoadingModels('constructive', threeModelData.id, constructive);
        if (this.cacheModels[cacheModelId] &&
            this.cacheModels[cacheModelId].isLoad) {
            this.clearLoadingModel('constructive', threeModelData.id, constructive);
            return;
        }

        modelPath = this.service.isOffline() ? OFFLINE_CONSTRUCTIVE :
            '/static-files/constructive/' + threeModelData.id.substring(0, 3) + '/' +
            threeModelData.id + '.' + threeModelData.ext;
        this.cacheModels[cacheModelId] = {isLoad: false, details: []};
        this.startTextureLoading();
        this.gltfLoader.load(modelPath, (gltf) => {
            let objects: Object3D[] = [];
            for (object of gltf.scene.children) {
                objects.push(object);
            }
            this.cacheModels[cacheModelId].isLoad = true;
            this.cacheModels[cacheModelId].details = objects;
            this.clearLoadingModel('constructive', threeModelData.id, constructive);
        }, () => {},
            () => {
                this.clearLoadingModel('constructive', threeModelData.id, constructive);
            });
    }

    public loadHandleThreeModel(handle: ThreeHandle) {
        let object: Object3D;
        let modelPath: string;
        let cacheModelId: string;

        if (!handle.threeModelData) {
            return;
        }
        const threeModelData: IHandleModelData = handle.threeModelData;
        cacheModelId = 'handle' + threeModelData.id;
        this.setLoadingModels('handle', threeModelData.id, handle);
        if (this.cacheModels[cacheModelId] &&
            this.cacheModels[cacheModelId].isLoad) {
            this.clearLoadingModel('handle', threeModelData.id, handle);
            return;
        }

        modelPath = this.getHandleModelLoadPath(handle.handleData, threeModelData);
        this.cacheModels[cacheModelId] = {isLoad: false, details: []};
        this.startTextureLoading();
        this.gltfLoader.load(modelPath, (gltf) => {
                let objects: Object3D[] = [];
                for (object of gltf.scene.children) {
                    // Mesh
                    // TODO !!!
                    if(object instanceof Mesh){
                        object.material.color = new Color(0Xafc9e1);
                        object.material.metalness = 1;
                        object.material.roughness = 0.4;
                        object.castShadow = true;
                        object.receiveShadow = true;
                    }
                    objects.push(object);
                }
                this.cacheModels[cacheModelId].isLoad = true;
                this.cacheModels[cacheModelId].details = objects;
                this.clearLoadingModel('handle', threeModelData.id, handle);
            }, () => {},
            () => {
                this.clearLoadingModel('handle', threeModelData.id, handle);
            });
    }

    public isLoaded(trySteps: number = 100): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            const interval = setInterval(() => {
                if (this.checkIsLoaded()) {
                    clearInterval(interval);
                    resolve(true);
                } else if (trySteps < 1) {
                    clearInterval(interval);
                    reject();
                }
                trySteps--;
            }, 100);
        });
    }

    public loadMaterialTextures(
        materialId: string,
        textures?: ITextureData[],
        textureChange?: ITextureChangeData[]
    ): IMaterialTextures {
        let textureData: ITextureData;
        let resultTextures: IMaterialTextures = {};
        let needLoadTextures: boolean = false;
        let cacheTexture: TCacheTexture;

        if (textures && textures.length > 0) {
            for (textureData of textures) {
                cacheTexture = this.loadTexture(
                    materialId,
                    textureData,
                    this.getTextureChangeByType(textureData.type, textureChange)
                );
                resultTextures[textureData.type] = cacheTexture.texture;
                if (!cacheTexture.isLoad) {
                    needLoadTextures = true;
                }
            }
        }

        if (needLoadTextures) {
            this.startTextureLoading();
        }

        return resultTextures;
    }

    public startTextureLoading() {
        if (this.loading) {
            return;
        }
        this.loading = true;
        this.showLoadingSpin();
        this.processTextureLoading();
    }

    protected getTextureChangeByType(
        textureType: TTextureType,
        textureChange?: ITextureChangeData[]): ITextureChangeData | undefined {
        if (!textureChange || !textureChange.length) {
            return undefined;
        }
        let textureChangeItem: ITextureChangeData;
        for (textureChangeItem of textureChange) {
            if (textureChangeItem.type === textureType) {
                return textureChangeItem;
            }
        }
        return undefined;
    }

    protected loadTexture(
        materialId: string,
        initTextureData: ITextureData,
        textureChange?: ITextureChangeData
    ): TCacheTexture {
        let textureId: string;
        let textureImage: {texture: Texture, isLoad: boolean};
        let textureData: ITextureData;

        textureData = {
            ...initTextureData,
            repeat: textureChange && textureChange.repeat ?
                {...textureChange.repeat} : {...initTextureData.repeat},
            offset:  textureChange && textureChange.offset ?
                {...textureChange.offset} : (initTextureData.offset ?
                    {...initTextureData.offset} : undefined),
        }
        textureId = CommonHelper.md5({
            id: materialId,
            type: textureData.type,
            repeat: textureData.repeat,
            offset: textureData.offset
        });
        if (this.cacheTextures[textureId]) {
            return this.cacheTextures[textureId];
        }
        textureImage = this.loadTextureImage(materialId, textureData, textureId);
        this.cacheTextures[textureId] = {
            isLoad: textureImage.isLoad,
            texture: textureImage.texture
        };

        return this.cacheTextures[textureId];
    }

    protected getHandleModelLoadPath(handleData: IHandleData, threeModelData: IHandleModelData): string {
        if (this.service.isOffline()) {
            return OFFLINE_HANDLE;
        }
        if (handleData.offerId) {
            return '/static-files/handleModel/' + threeModelData.id.substring(0, 3) + '/' +
                threeModelData.id + '.' + threeModelData.ext;
        }

        return '/static-files/handle/' + handleData.id + '/' +
            threeModelData.id + '.' + threeModelData.ext;
    }

    protected checkIsLoaded(): boolean {
        for (let index in this.cacheModels) {
            if (!this.cacheModels[index].isLoad) {
                return false;
            }
        }
        for (let index in this.cacheTextures) {
            if (!this.cacheTextures[index].isLoad) {
                return false;
            }
        }

        return true;
    }

    protected setLoadingModels(type: string, id: string, parent: CommonObject) {
        if (!this.loadingModels[type + id]) {
            this.loadingModels[type + id] = {type: type, parents: [parent]};
        } else {
            this.loadingModels[type + id].parents.push(parent);
        }
    }

    protected clearLoadingModel(type: string, id: string, parent: CommonObject) {
        let index: string;
        let parentItem: CommonObject;

        if (!this.loadingModels[type + id]) {
            console.warn('clearLoadingModel not found ', type + id);
            return;
        }
        for (index in this.loadingModels[type + id].parents) {
            parentItem = this.loadingModels[type + id].parents[index];
            if (parentItem.getId() === parent.getId()) {
                this.loadingModels[type + id].parents.splice(+index, 1);
            }
        }
        parent.setLoadModel(this.loadingModels[type + id].type, this.cacheModels[type + id].details);
        if (this.loadingModels[type + id].parents.length <= 0) {
            delete this.loadingModels[type + id];
        }
        if (Object.keys(this.loadingModels).length <= 0) {
            this.service.sendToRedux({type: LOADED_TEXTURES})
        }
    }

    protected getFacadeVersion(strVersion: string): TFacadeModelVersion {
        switch (strVersion) {
            case FACADE_MODEL_VERSION_2:
                return FACADE_MODEL_VERSION_2;
            case FACADE_MODEL_VERSION_1:
                return FACADE_MODEL_VERSION_1;
            case FACADE_MODEL_VERSION_NONE:
            default:
                return FACADE_MODEL_VERSION_NONE;
        }
    }

    protected getPoint3DbyString(strAxes?: string): TPoint3D {
        return {
            x: strAxes !== undefined && strAxes.toLowerCase().includes('x') ? 1 : 0,
            y: strAxes !== undefined && strAxes.toLowerCase().includes('y') ? 1 : 0,
            z: strAxes !== undefined && strAxes.toLowerCase().includes('z') ? 1 : 0
        }
    }

    protected getFacadeDetailType(
        name: string,
        version: TFacadeModelVersion,
        detailName: string
    ): TFacadeDetailType {
        if (version === FACADE_MODEL_VERSION_NONE) {
            name = detailName;
        }
        if (name.includes(FACADE_DETAIL_TYPE_CUSTOM_GLASS)) {
            return FACADE_DETAIL_TYPE_CUSTOM_GLASS;
        }
        if (name.includes(FACADE_DETAIL_TYPE_GLASS)) {
            return FACADE_DETAIL_TYPE_GLASS;
        }
        if (name.includes(FACADE_DETAIL_TYPE_BACK)) {
            return FACADE_DETAIL_TYPE_BACK;
        }
        if (name.includes(FACADE_DETAIL_TYPE_PATINA)) {
            return FACADE_DETAIL_TYPE_PATINA;
        }
        if (name.includes(FACADE_DETAIL_TYPE_SECOND)) {
            return FACADE_DETAIL_TYPE_SECOND;
        }
        if (name.includes(FACADE_DETAIL_TYPE_GRID)) {
            return FACADE_DETAIL_TYPE_GRID;
        }
        if (name.includes(FACADE_DETAIL_TYPE_CUSTOM)) {
            return FACADE_DETAIL_TYPE_CUSTOM;
        }
        if (name.includes(FACADE_DETAIL_TYPE_GENERAL)) {
            return FACADE_DETAIL_TYPE_MAIN;
        }
        return FACADE_DETAIL_TYPE_MAIN;
    }

    protected getFacadeTransformData(name: string): IModelTransformData {
        let transformData: IModelTransformData;
        let parseDetailName: IDetailNameTransformData;
        let version: TFacadeModelVersion;

        parseDetailName = this.parseTransformDataByDetailName(name);
        version = this.getFacadeVersion(parseDetailName.version);
        transformData = {
            version: version,
            detailType: this.getFacadeDetailType(parseDetailName.material, version, name),
            scale: this.getPoint3DbyString(parseDetailName.scale),
            translate: this.getPoint3DbyString(parseDetailName.translate)
        };

        return transformData;
    }

    protected parseTransformDataByDetailName(name: string): IDetailNameTransformData {
        let detailNameTransformData: IDetailNameTransformData;

        detailNameTransformData = {
            version: name.match(/v\(([^)]+)\)/)?.[1] || 'none',
            material: name.match(/m\(([^)]+)\)/)?.[1] || FACADE_DETAIL_TYPE_MAIN,
            scale: name.match(/s\(([^)]+)\)/)?.[1] || undefined,
            translate: name.match(/t\(([^)]+)\)/)?.[1] || undefined,

        };

        return detailNameTransformData;
    }

    protected loadTextureImage(materialId: string, textureData: ITextureData, textureId: string): {texture: Texture, isLoad: boolean} {
        let textureImageId: string;
        let texture: Texture;
        let imagePath: string;

        textureImageId = CommonHelper.md5({
            id: materialId,
            type: textureData.type,
            textureId: textureId
        });
        if (this.cacheTextureImages[textureImageId]) {
            if (textureData.repeat.x !== 1 || textureData.repeat.y !== 1 || textureData.offset || textureData.offset) {
                texture = this.cacheTextureImages[textureImageId].clone();
                texture.wrapT = RepeatWrapping;
                texture.wrapS = RepeatWrapping;
                texture.repeat.set(textureData.repeat.x, textureData.repeat.y);
                if (textureData.offset) {
                    texture.offset.set(textureData.offset.x, textureData.offset.y);
                }
                texture.needsUpdate = true;

                return {texture: texture, isLoad: true};
            }
            return {texture: this.cacheTextureImages[textureImageId], isLoad: true};
        }
        imagePath = this.service.isOffline() ? OFFLINE_TEXTURE : textureData.path
        this.cacheTextureImages[textureImageId] = this.textureLoader.load(imagePath, () => {
            this.cacheTextures[textureId].texture.wrapT = RepeatWrapping;
            this.cacheTextures[textureId].texture.wrapS = RepeatWrapping;
            this.cacheTextures[textureId].texture.repeat.set(textureData.repeat.x, textureData.repeat.y);
            if (textureData.offset) {
                this.cacheTextures[textureId].texture.offset.set(textureData.offset.x, textureData.offset.y);
            }
            this.cacheTextures[textureId].isLoad = true;
            this.cacheTextures[textureId].texture.needsUpdate = true;
        });

        return {texture: this.cacheTextureImages[textureImageId], isLoad: false};
    }

    protected stopTextureLoading() {
        this.loading = false;
        this.service.sendToRedux({type: LOADED_TEXTURES})
        this.hideLoadingSpin();
    }

    protected showLoadingSpin() {
        this.service.sendToRedux({type: SHOW_TEXTURE_LOADING})
    }

    protected hideLoadingSpin() {
        this.service.sendToRedux({type: HIDE_TEXTURE_LOADING})
    }

    protected processTextureLoading() {
        if (this.checkIsLoaded()) {
            setTimeout(() => {
                this.stopTextureLoading();
            }, 100);
        } else {
            setTimeout(() => {
                this.processTextureLoading();
            }, 100);
        }
    }

    private static initTextures(): ITextureCache {
        return {
            room: {
                floor: {map: null},
                wall: {map: null}
            }
        };
    }

    private static initSketchMaterials() {
        return {
            // default: new MeshBasicMaterial({color: '#ede8c2', transparent: true, opacity: 0.5}),
            // default: new MeshBasicMaterial({color: '#ede8c2'}),
            default: new MeshBasicMaterial({
                color: 0xffdd7b,
                side: DoubleSide,
                transparent: true,
                opacity: 0.6,
                precision: 'highp'
            }),
            room: new MeshBasicMaterial({color: '#cecece'})
        }
    }
}